import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services';

interface ClaimRouteResult {
  assignmentId: string;
  date: string;
}

/** Self-assigns the given route for today. Throws on conflict (already taken). */
export const claimRoute = async (routeId: string): Promise<ClaimRouteResult> => {
  const callable = httpsCallable<{ routeId: string }, ClaimRouteResult>(functions, 'claimRoute');
  const { data } = await callable({ routeId });
  return data;
};

/** Self-unassigns a route the caller currently holds for today. */
export const unassignRoute = async (routeId: string): Promise<{ date: string }> => {
  const callable = httpsCallable<{ routeId: string }, { date: string }>(functions, 'unassignRoute');
  const { data } = await callable({ routeId });
  return data;
};

interface ExtraAssignmentResult {
  assignmentId: string;
  date: string;
}

/** Adds one Extra selection for today; repeated selections are allowed. */
export const selectExtra = async (routeId: string): Promise<ExtraAssignmentResult> => {
  const callable = httpsCallable<{ routeId: string }, ExtraAssignmentResult>(
    functions,
    'selectExtra',
  );
  const { data } = await callable({ routeId });
  return data;
};

/** Withdraws one of the caller's Extra selections for today. */
export const withdrawExtra = async (assignmentId: string): Promise<{ date: string }> => {
  const callable = httpsCallable<{ assignmentId: string }, { date: string }>(
    functions,
    'withdrawExtra',
  );
  const { data } = await callable({ assignmentId });
  return data;
};

/** Admin: adds a repeatable Extra selection for a user on a chosen day. */
export const adminSelectExtra = async (
  routeId: string,
  date: string,
  userId: string,
): Promise<ExtraAssignmentResult> => {
  const callable = httpsCallable<
    { routeId: string; date: string; userId: string },
    ExtraAssignmentResult
  >(functions, 'adminSelectExtra');
  const { data } = await callable({ routeId, date, userId });
  return data;
};

/** Admin: withdraws one Extra selection, keeping its history record. */
export const adminWithdrawExtra = async (assignmentId: string): Promise<{ date: string }> => {
  const callable = httpsCallable<{ assignmentId: string }, { date: string }>(
    functions,
    'adminWithdrawExtra',
  );
  const { data } = await callable({ assignmentId });
  return data;
};

interface AdminAssignmentResult {
  assignmentId: string;
  date: string;
}

/** Admin: assigns a currently-unclaimed route to a specific user on a given date. */
export const adminAssignRoute = async (
  routeId: string,
  date: string,
  userId: string,
): Promise<AdminAssignmentResult> => {
  const callable = httpsCallable<
    { routeId: string; date: string; userId: string },
    AdminAssignmentResult
  >(functions, 'adminAssignRoute');
  const { data } = await callable({ routeId, date, userId });
  return data;
};

/** Admin: moves a route (claimed or not) to a different user on a given date. */
export const adminReassignRoute = async (
  routeId: string,
  date: string,
  userId: string,
): Promise<AdminAssignmentResult> => {
  const callable = httpsCallable<
    { routeId: string; date: string; userId: string },
    AdminAssignmentResult
  >(functions, 'adminReassignRoute');
  const { data } = await callable({ routeId, date, userId });
  return data;
};

/** Admin: unassigns whoever currently holds a route on a given date. */
export const adminUnassignRoute = async (
  routeId: string,
  date: string,
): Promise<{ date: string }> => {
  const callable = httpsCallable<{ routeId: string; date: string }, { date: string }>(
    functions,
    'adminUnassignRoute',
  );
  const { data } = await callable({ routeId, date });
  return data;
};
